import { FormEvent, useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { api, ApiError } from "@/lib/api";
import {
  passkeysSupported,
  serializeRegistrationCredential,
  toRegistrationArgs,
} from "@/features/auth/two-factor";

type TwoFactorStatus = {
  enabled: boolean;
  totpEnabled: boolean;
  emailEnabled: boolean;
  webauthnEnabled: boolean;
  preferredMethod: "totp" | "email" | "webauthn" | null;
  hasRecoveryCodes: boolean;
  recoveryCodeCount: number;
  credentialCount: number;
  credentialNames: string[];
  credentials: { id: string; name: string }[];
};

function useTwoFactorStatus() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/status",
      );
      setStatus(result.status);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not load 2FA status.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { status, loading, error, reload, setStatus };
}

function TotpSection({
  status,
  onChange,
}: {
  status: TwoFactorStatus;
  onChange: (s: TwoFactorStatus) => void;
}) {
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ secret: string; otpauthUrl: string }>(
        "/api/auth/2fa/totp/setup",
        { method: "POST" },
      );
      setSecret(result.secret);
      setOtpauthUrl(result.otpauthUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start setup.");
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api<{
        recoveryCodes: string[] | null;
        status?: TwoFactorStatus;
      }>("/api/auth/2fa/totp/confirm", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      if (result.recoveryCodes) setCodes(result.recoveryCodes);
      setSecret(null);
      setOtpauthUrl(null);
      setCode("");
      const fresh = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/status",
      );
      onChange(fresh.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Code didn't match.");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!window.confirm("Disable authenticator app 2FA?")) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/totp/disable",
        { method: "POST" },
      );
      onChange(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not disable.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-rs-ink">Authenticator app</p>
          <p className="text-xs text-rs-ink-muted">
            Time-based codes from Google Authenticator, 1Password, etc.
          </p>
        </div>
        {status.totpEnabled ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => void disable()}
          >
            Disable
          </Button>
        ) : secret ? null : (
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => void start()}
          >
            Set up
          </Button>
        )}
      </div>

      {codes ? (
        <div className="space-y-2 rounded-rs-sm border border-rs-border bg-rs-muted p-3">
          <p className="text-sm font-medium text-rs-ink">
            Save these recovery codes now
          </p>
          <p className="text-xs text-rs-ink-muted">
            Each code works once if you lose your second factor. They won’t be
            shown again.
          </p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-rs-ink">
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <Button size="sm" variant="secondary" onClick={() => setCodes(null)}>
            I saved them
          </Button>
        </div>
      ) : null}

      {secret && !status.totpEnabled ? (
        <div className="space-y-3 rounded-rs-sm border border-rs-border p-3">
          <p className="text-xs text-rs-ink-muted">
            Scan with your authenticator app, or enter this secret manually:
          </p>
          <p className="break-all font-mono text-sm text-rs-ink">{secret}</p>
          <a
            className="text-xs text-rs-accent-ink underline-offset-2 hover:underline"
            href={otpauthUrl ?? "#"}
          >
            Open in authenticator app
          </a>
          <form className="flex gap-2" onSubmit={confirm}>
            <Input
              aria-label="Authenticator code"
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={busy}>
              {busy ? "Verifying…" : "Verify"}
            </Button>
          </form>
        </div>
      ) : null}

      {error ? (
        <p className="text-xs text-rs-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function EmailSection({
  status,
  onChange,
}: {
  status: TwoFactorStatus;
  onChange: (s: TwoFactorStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/email/enable",
        {
          method: "POST",
          body: JSON.stringify({ enabled: !status.emailEnabled }),
        },
      );
      onChange(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-rs-ink">Email code</p>
          <p className="text-xs text-rs-ink-muted">
            A 6-digit code emailed to you at sign-in. Good backup method.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void toggle()}
        >
          {status.emailEnabled ? "Disable" : "Enable"}
        </Button>
      </div>
      {error ? (
        <p className="text-xs text-rs-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PasskeySection({
  status,
  onChange,
}: {
  status: TwoFactorStatus;
  onChange: (s: TwoFactorStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");

  async function register() {
    if (!passkeysSupported()) {
      setError("Passkeys are not supported in this browser.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { options } = await api<{
        options: Parameters<typeof toRegistrationArgs>[0];
      }>("/api/auth/2fa/webauthn/register/start", { method: "POST" });
      const credential = await navigator.credentials.create(
        toRegistrationArgs(options),
      );
      if (!credential) throw new Error("Passkey creation was cancelled.");
      const result = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/webauthn/register/finish",
        {
          method: "POST",
          body: JSON.stringify({
            credential: serializeRegistrationCredential(credential),
            name: name.trim() || undefined,
          }),
        },
      );
      setName("");
      onChange(result.status);
    } catch (err) {
      setError(
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Passkey creation was cancelled or timed out."
          : err instanceof Error
            ? err.message
            : "Could not register passkey.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(credentialId: string) {
    if (!window.confirm("Remove this passkey?")) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/webauthn/remove",
        { method: "POST", body: JSON.stringify({ credentialId }) },
      );
      onChange(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 px-5 py-4">
      <div>
        <p className="text-sm font-medium text-rs-ink">
          Passkey{" "}
          <span className="text-xs font-normal text-rs-ink-muted">
            (recommended)
          </span>
        </p>
        <p className="text-xs text-rs-ink-muted">
          Fingerprint, face, or security key. Strongest phishing-resistant
          second factor.
        </p>
      </div>
      {status.credentials.length > 0 ? (
        <ul className="space-y-1.5">
          {status.credentials.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="text-rs-ink">{c.name}</span>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void remove(c.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-rs-ink-muted">No passkeys enrolled yet.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Input
          aria-label="Passkey name"
          placeholder="Passkey name (e.g. MacBook)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="max-w-56"
        />
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void register()}
        >
          {busy ? "Waiting…" : "Add passkey"}
        </Button>
      </div>
      {error ? (
        <p className="text-xs text-rs-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RecoverySection({
  status,
  onChange,
}: {
  status: TwoFactorStatus;
  onChange: (s: TwoFactorStatus) => void;
}) {
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    if (
      status.hasRecoveryCodes &&
      !window.confirm(
        "Generating new codes invalidates your existing ones. Continue?",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ codes: string[]; status: TwoFactorStatus }>(
        "/api/auth/2fa/recovery/generate",
        { method: "POST" },
      );
      setCodes(result.codes);
      onChange(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not generate.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 px-5 py-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-rs-ink">Recovery codes</p>
          <p className="text-xs text-rs-ink-muted">
            {status.hasRecoveryCodes
              ? `${status.recoveryCodeCount} unused codes remaining. Each works once.`
              : "One-time codes for when you lose your second factor."}
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void generate()}
        >
          {status.hasRecoveryCodes ? "Generate new" : "Generate"}
        </Button>
      </div>
      {codes ? (
        <div className="space-y-2 rounded-rs-sm border border-rs-border bg-rs-muted p-3">
          <p className="text-xs text-rs-ink-muted">
            Save these now — they won’t be shown again.
          </p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-rs-ink">
            {codes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <Button size="sm" variant="secondary" onClick={() => setCodes(null)}>
            I saved them
          </Button>
        </div>
      ) : null}
      {error ? (
        <p className="text-xs text-rs-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function PreferredSection({
  status,
  onChange,
}: {
  status: TwoFactorStatus;
  onChange: (s: TwoFactorStatus) => void;
}) {
  const [busy, setBusy] = useState(false);
  const options = [
    { value: "webauthn", label: "Passkey", enabled: status.webauthnEnabled },
    { value: "totp", label: "Authenticator app", enabled: status.totpEnabled },
    { value: "email", label: "Email code", enabled: status.emailEnabled },
  ].filter((o) => o.enabled);
  if (options.length < 2) return null;

  async function setPreferred(method: string) {
    setBusy(true);
    try {
      const result = await api<{ status: TwoFactorStatus }>(
        "/api/auth/2fa/preferred",
        { method: "POST", body: JSON.stringify({ method }) },
      );
      onChange(result.status);
    } catch (err) {
      if (err instanceof ApiError) {
        // Surface via alert-less inline handling: status reload shows truth.
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 px-5 py-4">
      <p className="text-sm font-medium text-rs-ink">Preferred method</p>
      <p className="text-xs text-rs-ink-muted">
        Offered first at sign-in. Passkeys are recommended.
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <Button
            key={o.value}
            size="sm"
            variant={
              status.preferredMethod === o.value ? "primary" : "secondary"
            }
            disabled={busy}
            onClick={() => void setPreferred(o.value)}
          >
            {o.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function SettingsSecurityTab() {
  const { status, loading, error, setStatus } = useTwoFactorStatus();

  if (loading) {
    return (
      <Panel>
        <PanelHeader title="Sign-in & security" />
        <p className="px-5 py-4 text-sm text-rs-ink-muted">Loading…</p>
      </Panel>
    );
  }

  if (!status || error) {
    return (
      <Panel>
        <PanelHeader title="Sign-in & security" />
        <p className="px-5 py-4 text-sm text-rs-danger" role="alert">
          {error ?? "Could not load security settings."}
        </p>
      </Panel>
    );
  }

  const set = (s: TwoFactorStatus) => setStatus(s);

  return (
    <div className="space-y-5">
      <Panel>
        <PanelHeader
          title="Two-factor authentication"
          description={
            status.enabled
              ? "Enabled. You’ll be asked for a second factor at sign-in."
              : "Disabled. Turn on at least one method below to protect your account."
          }
        />
        <div className="divide-y divide-rs-border">
          <PasskeySection status={status} onChange={set} />
          <TotpSection status={status} onChange={set} />
          <EmailSection status={status} onChange={set} />
          <PreferredSection status={status} onChange={set} />
          <RecoverySection status={status} onChange={set} />
        </div>
      </Panel>
    </div>
  );
}
